import "./ask-page-shell.css";
import "~/modules/askgvt/askgvt.css";
import "~/modules/askgvt/ask-page/AskPageApp.css";
import { AskConsumerLayout } from "@ui/layouts/AskConsumerLayout/AskConsumerLayout";
import type { QuestionBoxHandle } from "@ui/organisms/QuestionBox/QuestionBox";
import type { QuestionContextVideo } from "@ui/organisms/QuestionBox/QuestionContextVideos";
import type { AcceptedEffortUpgrade } from "@ui/organisms/QuestionBox/useUpgradableEffort";
import { useIsMobile } from "@utils/responsive";
import { createSignal, onCleanup, Show } from "solid-js";
import { render } from "solid-js/web";
import type { ChatPanelController } from "~/modules/shared/chat/chat-panel/ChatPanel";
import { OffersClipboardProvider } from "~/modules/shared/chat/components/clipboard/offersClipboard";
import { OffersVideoReportProvider } from "~/modules/shared/chat/components/report/offersVideoReport";
import { AskPageThreadMobileInterface } from "./askPageThreadMobileInterface";
import { AskPageClipboard } from "./components/AskPageClipboard";
import { AskPageFollowUp } from "./components/AskPageFollowUp";
import { AskPageHeader } from "./components/AskPageHeader";
import { AskPageSidePane } from "./components/AskPageSidePane";
import { AskPageThread, type ThreadQuestion } from "./components/AskPageThread";
import { SharedSnapshotGatedPreview } from "./components/SharedSnapshotGatedPreview";
import type { AskedQuestion } from "./components/useAskPageForm";

/**
 * The public entry point for a shared, static snapshot of an AskPage
 * thread — sibling to ask.tsx/ask-question.tsx. A revoked/expired share never
 * reaches this page at all: PublicAskPageShareController::show() renders a
 * BlockingErrorPage instead.
 */
declare const pageVars: {
	influencer_id: string;
	influencer_handle: string;
	influencer_display_name: string;
	influencer_avatar_url: string | null;
	banner_url: string | null;
	banner_web_url?: string | null;
	sharePublicId: string;
	isSignedIn: boolean;
	paysForOwnAskPageQuestions?: boolean;
	/** False on a paid share the viewer can only see a preview of. */
	canFollowUp: boolean;
	appEmbedded?: boolean;
	gated: boolean;
	threadQuestions: ThreadQuestion[];
	gatedPrompts: string[];
};

export const SharedThreadPage = () => {
	const [threadComplete, setThreadComplete] = createSignal(false);
	const [contextVideos, setContextVideos] = createSignal<
		QuestionContextVideo[]
	>([]);
	const [followUp, setFollowUp] = createSignal<QuestionBoxHandle>();
	const [acceptedEffortUpgrade, setAcceptedEffortUpgrade] =
		createSignal<AcceptedEffortUpgrade>();
	let controller: ChatPanelController | undefined;
	const isMobile = useIsMobile();

	// The follow-up is in the viewer's own fork of the share, which holds the
	// answers already on screen, so the page carries on from the fork instead
	// of navigating to it and replaying every answer.
	const showFollowUp = (asked: AskedQuestion) => {
		setThreadComplete(false);
		window.history.replaceState(null, "", asked.path);
		document.title = `Ask ${pageVars.influencer_display_name}`;
		controller?.continueConversation(
			`/api/public/ask-page/${pageVars.influencer_handle}/questions/${asked.id}/stream`,
		);
	};

	// Paid-access refusals are left to the server, whose answer says what the
	// visitor can do about it.
	const mobileView = pageVars.appEmbedded
		? new AskPageThreadMobileInterface({
				handle: pageVars.influencer_handle,
				threadRootId: () => pageVars.threadQuestions.at(-1)?.id ?? "",
				effort: () => acceptedEffortUpgrade()?.effort,
				refusal: () => {
					if (pageVars.gated || pageVars.threadQuestions.length === 0) {
						return "share_gated";
					}
					if (!threadComplete()) {
						return "answer_in_progress";
					}
					return undefined;
				},
				onAsked: showFollowUp,
			})
		: undefined;
	if (mobileView) {
		window.view = mobileView;
		onCleanup(() => {
			window.view = null;
		});
	}

	return (
		<AskConsumerLayout
			title="Shared answer"
			backLink={`/@${pageVars.influencer_handle}`}
			titleSize="small"
			hasHeaderMargin={!isMobile()}
			// The mobile top bar is header enough, as on the thread page.
			showHeader={!isMobile()}
			signedOutTopNav
			condenseGuestTopNavOnMobile
		>
			<OffersVideoReportProvider enabled={pageVars.isSignedIn}>
				<OffersClipboardProvider enabled={!pageVars.appEmbedded}>
					<div class="askgvt1-ask-page-shell">
						<div class="askgvt1-creator-page">
							<div class="askgvt1-thread-column">
								<Show when={!pageVars.appEmbedded}>
									<AskPageHeader
										compact
										displayName={pageVars.influencer_display_name}
										avatarUrl={pageVars.influencer_avatar_url}
										bannerUrl={pageVars.banner_web_url ?? pageVars.banner_url}
										tagline="Shared answer"
										profileUrl={`/@${pageVars.influencer_handle}`}
									/>
								</Show>
								<div class="askgvt1-thread-shell">
									<Show
										when={!pageVars.gated}
										fallback={
											<SharedSnapshotGatedPreview
												prompts={pageVars.gatedPrompts}
												isSignedIn={pageVars.isSignedIn}
											/>
										}
									>
										<AskPageThread
											handle={pageVars.influencer_handle}
											questions={pageVars.threadQuestions}
											onComplete={() => setThreadComplete(true)}
											onContextVideos={setContextVideos}
											onAcceptedEffortUpgrade={setAcceptedEffortUpgrade}
											onController={(thread) => {
												controller = thread;
												mobileView?.setController(thread);
											}}
										/>
									</Show>
									<AskPageClipboard
										followUp={followUp()}
										canAddToBoard={false}
									/>
									<Show when={!pageVars.appEmbedded}>
										<AskPageFollowUp
											handle={pageVars.influencer_handle}
											influencerId={pageVars.influencer_id}
											displayName={pageVars.influencer_display_name}
											threadRootId={pageVars.threadQuestions.at(-1)?.id ?? ""}
											contextVideos={contextVideos()}
											isSignedIn={pageVars.isSignedIn}
											paysOwnCredits={pageVars.paysForOwnAskPageQuestions}
											canFollowUp={
												pageVars.canFollowUp &&
												pageVars.threadQuestions.length > 0
											}
											disabled={!threadComplete()}
											onReady={setFollowUp}
											onAsked={showFollowUp}
											acceptedEffortUpgrade={acceptedEffortUpgrade()}
										/>
									</Show>
								</div>
							</div>
						</div>
						<AskPageSidePane
							showAddToBoardButton={false}
							isSignedIn={pageVars.isSignedIn}
						/>
					</div>
				</OffersClipboardProvider>
			</OffersVideoReportProvider>
		</AskConsumerLayout>
	);
};

document.addEventListener("DOMContentLoaded", () => {
	const mount = document.getElementById("ask-page-root") ?? document.body;
	render(() => <SharedThreadPage />, mount);
});
